import { useState, useMemo, useEffect } from "react";
import { format, eachDayOfInterval, parseISO, isWithinInterval, isSameDay } from "date-fns";
import { useIsMobile } from "@/hooks/use-mobile";
import * as SelectPrimitive from "@radix-ui/react-select";
import { checkShiftOverlap, formatOverlapDescription, type ShiftForOverlap } from "@/utils/shiftOverlapUtils";

type Profile = { id: string; first_name?: string; last_name?: string; full_name?: string };

function displayName(p: Profile) {
  const full = (p.full_name ?? "").trim();
  if (full) return full;
  const fallback = [p.first_name, p.last_name].filter(Boolean).join(" ").trim();
  return fallback || "Unnamed";
}

const NONE_VALUE = "__NONE__";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogOverlay,
  DialogPortal,
} from "@/components/ui/dialog";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerFooter,
} from "@/components/ui/drawer";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

type CreateContext = "empty-week" | "cell" | "toolbar";

interface CreateShiftDialogProps {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  context: CreateContext;
  weekStartISO?: string;
  weekEndISO?: string;
  initialDateISO?: string;
  initialStartHHmm?: string;
  initialEndHHmm?: string;
  profiles: Array<{ id: string; first_name?: string; last_name?: string; full_name?: string }>;
  defaultStart?: string;
  defaultEnd?: string;
  allowOpenShifts?: boolean;
  existingShifts?: ShiftForOverlap[]; // For overlap detection
  onCreate: (args: {
    profile_id: string | null;
    start_time: string;
    end_time: string;
    note: string | null;
  }) => Promise<void>;
}

export function CreateShiftDialog({
  open,
  onOpenChange,
  context,
  weekStartISO,
  weekEndISO,
  initialDateISO,
  initialStartHHmm,
  initialEndHHmm,
  profiles,
  defaultStart = "09:00",
  defaultEnd = "10:00",
  allowOpenShifts = false,
  existingShifts = [],
  onCreate,
}: CreateShiftDialogProps) {
  const isMobile = useIsMobile();

  // Week days for chips (only for empty-week context)
  const weekDays = useMemo(() => {
    if (!weekStartISO || !weekEndISO) return [];
    return eachDayOfInterval({ 
      start: parseISO(weekStartISO), 
      end: parseISO(weekEndISO) 
    });
  }, [weekStartISO, weekEndISO]);

  // Default date: today if inside week else first day
  const [date, setDate] = useState<Date>(() => {
    if (context === "empty-week") {
      const today = new Date();
      if (weekStartISO && weekEndISO && 
          isWithinInterval(today, { 
            start: parseISO(weekStartISO), 
            end: parseISO(weekEndISO) 
          })) {
        return today;
      }
      return weekDays[0] ?? today;
    }
    return initialDateISO ? parseISO(initialDateISO) : new Date();
  });

  // Form state
  const [selectedEmployee, setSelectedEmployee] = useState("");
  const [startTime, setStartTime] = useState(initialStartHHmm || defaultStart);
  const [endTime, setEndTime] = useState(initialEndHHmm || defaultEnd);
  const [note, setNote] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Overlap warning state
  const [overlapWarning, setOverlapWarning] = useState<{
    show: boolean;
    overlappingShifts: ShiftForOverlap[];
    pendingShiftData: {
      profile_id: string | null;
      start_time: string;
      end_time: string;
      note: string | null;
    } | null;
  } | null>(null);

  // Reset form when dialog opens or context changes
  useEffect(() => {
    if (open) {
      setSelectedEmployee("");
      setStartTime(initialStartHHmm || defaultStart);
      setEndTime(initialEndHHmm || defaultEnd);
      setNote("");
      setIsSubmitting(false);
      setOverlapWarning(null); // Reset overlap warning
      
      // Reset date for empty-week context
      if (context === "empty-week") {
        const today = new Date();
        if (weekStartISO && weekEndISO && 
            isWithinInterval(today, { 
              start: parseISO(weekStartISO), 
              end: parseISO(weekEndISO) 
            })) {
          setDate(today);
        } else if (weekDays.length > 0) {
          setDate(weekDays[0]);
        }
      } else if (initialDateISO) {
        setDate(parseISO(initialDateISO));
      }
    }
  }, [open, context, initialStartHHmm, initialEndHHmm, initialDateISO, weekStartISO, weekEndISO, defaultStart, defaultEnd, weekDays]);

  // Combine date + time into local Date objects
  const combineLocal = (d: Date, hhmm: string) => {
    const [h, m] = hhmm.split(":").map(Number);
    const out = new Date(d);
    out.setHours(h, m, 0, 0);
    return out;
  };

  const hasProfiles = Array.isArray(profiles) && profiles.length > 0;

  // Debug logging to verify data flow
  console.debug("[CreateShiftDialog] profiles:", profiles?.length);

  // Validation logic
  const validTimeRange = startTime && endTime && startTime < endTime;
  const canSubmit = !isSubmitting && validTimeRange && (!!selectedEmployee || allowOpenShifts);

  // Actually create the shift (called after overlap check or confirmation)
  const proceedWithCreate = async (shiftData: {
    profile_id: string | null;
    start_time: string;
    end_time: string;
    note: string | null;
  }) => {
    setIsSubmitting(true);
    try {
      await onCreate(shiftData);
      setOverlapWarning(null);
      onOpenChange(false);
    } catch (error) {
      console.error('Error creating shift:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async () => {
    if (!canSubmit) {
      return;
    }

    const chosenDate = context === "empty-week" 
      ? date 
      : (initialDateISO ? parseISO(initialDateISO) : new Date());
    
    const start = combineLocal(chosenDate, startTime);
    const end = combineLocal(chosenDate, endTime);
    
    const profileId = selectedEmployee || null;
    
    const shiftData = {
      profile_id: profileId,
      start_time: start.toISOString(),
      end_time: end.toISOString(),
      note: note?.trim() || null,
    };

    // Check for overlapping shifts
    const overlapResult = checkShiftOverlap(shiftData, existingShifts);
    
    if (overlapResult.hasOverlap) {
      // Show confirmation dialog
      setOverlapWarning({
        show: true,
        overlappingShifts: overlapResult.overlappingShifts,
        pendingShiftData: shiftData,
      });
      return;
    }

    // No overlap, proceed directly
    await proceedWithCreate(shiftData);
  };

  const handleOverlapConfirm = async () => {
    if (overlapWarning?.pendingShiftData) {
      await proceedWithCreate(overlapWarning.pendingShiftData);
    }
  };

  const handleOverlapCancel = () => {
    setOverlapWarning(null);
  };

  // Form body component (shared between Dialog and Drawer)
  const formBody = (
    <div className="space-y-4">
      {/* Date chips - only for empty-week context */}
      {context === "empty-week" && (
        <div className="space-y-2">
          <Label>Date</Label>
          <div 
            role="listbox" 
            aria-label="Select a day this week" 
            className="flex flex-wrap gap-2"
          >
            {weekDays.map((d) => {
              const selected = isSameDay(date, d);
              return (
                <button
                  key={d.toISOString()}
                  type="button"
                  role="option"
                  aria-selected={selected}
                  onClick={() => setDate(d)}
                  onKeyDown={(e) => {
                    if (e.key === "ArrowRight" || e.key === "ArrowLeft") {
                      e.preventDefault();
                      const i = weekDays.findIndex(x => isSameDay(x, date));
                      const next = e.key === "ArrowRight" 
                        ? Math.min(i + 1, weekDays.length - 1) 
                        : Math.max(i - 1, 0);
                      setDate(weekDays[next]);
                    }
                  }}
                  className={[
                    "px-3 py-1 rounded-full border text-sm transition",
                    selected
                      ? "bg-[#0B1220] text-white border-[#0B1220] shadow"
                      : "bg-white text-foreground border-muted-foreground/30 hover:bg-muted"
                  ].join(" ")}
                >
                  {format(d, "EEE dd")}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Employee selection */}
      <div>
        <Label>Employee{allowOpenShifts ? " (optional)" : ""}</Label>
        {hasProfiles ? (
          <div className="space-y-2">
            <Select 
              value={selectedEmployee || NONE_VALUE} 
              onValueChange={(value) => setSelectedEmployee(value === NONE_VALUE ? "" : value)}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select an employee" />
              </SelectTrigger>
              
              {/* Portal the menu to body and raise z-index above Dialog/Drawer */}
              <SelectPrimitive.Portal container={typeof document !== "undefined" ? document.body : undefined}>
                <SelectContent
                  className="z-[80] max-h-72 overflow-auto"
                  position="popper"
                  sideOffset={8}
                >
                  {allowOpenShifts && (
                    <SelectItem value={NONE_VALUE}>None (Open shift)</SelectItem>
                  )}
                  {profiles.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {displayName(p)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </SelectPrimitive.Portal>
            </Select>
            {allowOpenShifts && (
              <p className="text-sm text-muted-foreground">
                Select "None (Open shift)" to create an open shift that employees can claim.
              </p>
            )}
          </div>
        ) : (
          <div className="space-y-2">
            {allowOpenShifts ? (
              <Select 
                value={NONE_VALUE} 
                onValueChange={(value) => setSelectedEmployee(value === NONE_VALUE ? "" : value)}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Create open shift" />
                </SelectTrigger>
                <SelectPrimitive.Portal container={typeof document !== "undefined" ? document.body : undefined}>
                  <SelectContent
                    className="z-[80] max-h-72 overflow-auto"
                    position="popper"
                    sideOffset={8}
                  >
                    <SelectItem value={NONE_VALUE}>None (Open shift)</SelectItem>
                  </SelectContent>
                </SelectPrimitive.Portal>
              </Select>
            ) : (
              <Select disabled>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="No employees available" />
                </SelectTrigger>
              </Select>
            )}
            {allowOpenShifts ? (
              <p className="text-sm text-muted-foreground">
                You can create open shifts that employees can claim. Add team members in <a href="/team" className="underline">Team</a> to assign shifts directly.
              </p>
            ) : (
              <p className="text-sm text-muted-foreground">
                Add team members in <a href="/team" className="underline">Team</a> to assign shifts.
              </p>
            )}
          </div>
        )}
      </div>

      {/* Time inputs */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <Label htmlFor="start-time">Start Time</Label>
          <Input
            id="start-time"
            type="time"
            value={startTime}
            onChange={(e) => setStartTime(e.target.value)}
          />
        </div>
        <div>
          <Label htmlFor="end-time">End Time</Label>
          <Input
            id="end-time"
            type="time"
            value={endTime}
            onChange={(e) => setEndTime(e.target.value)}
          />
        </div>
      </div>

      {/* Note */}
      <div>
        <Label htmlFor="note">Note (Optional)</Label>
        <Textarea
          id="note"
          placeholder="Add any notes about this shift..."
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
      </div>
    </div>
  );

  // Button group component (shared between Dialog and Drawer)
  const buttonGroup = (
    <div className="flex flex-col gap-2 sm:flex-row sm:justify-end sm:gap-2">
      <Button 
        variant="outline" 
        onClick={() => onOpenChange(false)}
        disabled={isSubmitting}
      >
        Cancel
      </Button>
      <Button 
        onClick={handleSubmit}
        disabled={!canSubmit}
      >
        {isSubmitting ? "Creating..." : "Create Shift"}
      </Button>
    </div>
  );

  // Overlap warning AlertDialog (shared between mobile and desktop)
  const overlapAlertDialog = (
    <AlertDialog open={overlapWarning?.show} onOpenChange={(open) => !open && handleOverlapCancel()}>
      <AlertDialogContent className="z-[80]">
        <AlertDialogHeader>
          <AlertDialogTitle>Overlapping Shift Detected</AlertDialogTitle>
          <AlertDialogDescription>
            This shift overlaps with an existing shift for the same employee:
            <span className="block mt-2 font-medium text-foreground">
              {overlapWarning?.overlappingShifts && formatOverlapDescription(overlapWarning.overlappingShifts)}
            </span>
            <span className="block mt-2">
              Do you want to create this shift anyway?
            </span>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={handleOverlapCancel}>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={handleOverlapConfirm}>
            Create Anyway
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );

  if (isMobile) {
    // Mobile: bottom sheet Drawer
    return (
      <>
        <Drawer open={open} onOpenChange={onOpenChange}>
          <DrawerContent className="z-[60]">
            <DrawerHeader>
              <DrawerTitle>Create New Shift</DrawerTitle>
            </DrawerHeader>
            <div className="px-4 pb-4">
              {formBody}
            </div>
            <DrawerFooter className="px-4">
              {buttonGroup}
            </DrawerFooter>
          </DrawerContent>
        </Drawer>
        {overlapAlertDialog}
      </>
    );
  }

  // Desktop: Dialog via Portal
  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogPortal container={typeof document !== "undefined" ? document.body : undefined}>
          <DialogOverlay className="fixed inset-0 z-[60] bg-black/50" />
          <DialogContent className="z-[60] sm:max-w-[480px]" aria-describedby="create-shift-description">
            <DialogHeader>
              <DialogTitle>Create New Shift</DialogTitle>
            </DialogHeader>
            <div id="create-shift-description" className="sr-only">
              Dialog for creating a new shift with employee assignment, time selection, and optional notes.
            </div>
            <div className="space-y-4">
              {formBody}
            </div>
            <DialogFooter>
              {buttonGroup}
            </DialogFooter>
          </DialogContent>
        </DialogPortal>
      </Dialog>
      {overlapAlertDialog}
    </>
  );
}
